import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ManageTimesheetComponent } from './manage-timesheet.component';
import { TimesheetService } from '../services/timesheet.service';
import { Timesheet } from '../shared/models/timesheet';
import { TimesheetSearchModel } from '../shared/models/timesheet-search-model';
import { PpsUtilityService } from '../services/pps-utility.service';
import { IEntity } from '../shared/models/entity';
import { Employee } from '../shared/models/employee';
import { ToastService } from '../shared/toast/toast.service';
import { TimesheetActionComponent } from './timesheet-action.component';
import { TimesheetFormComponent } from './timesheet-form.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { IJobCode } from '../shared/models/job-code';
import { IRosterCode } from '../shared/models/roster-code';
import { AuthenticationService } from '../services/authentication.service';
import { UserContext } from '../shared/models/user-context';
import { BusinessOperation } from '../shared/models/business-operation';
import { format, isSameDay } from 'date-fns';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { EmployeeBase } from '../shared/models/employee-base';
import { SubSink } from 'subsink';
import * as _ from 'lodash';
import { GridReadyEvent } from 'ag-grid-community';
import { IEmployeeTimesheet } from '../shared/models/employee-timesheet';
import { WarningType } from '../shared/models/warning-type';
import { GenerateTimesheetComponent } from './generate-timesheet-form.component';
import { TimesheetGenerationModel } from '../shared/models/timesheet-generation-model';
import { IEmployer } from '../shared/models/employer';

@Component({
  selector: 'pps-timesheet',
  templateUrl: './timesheet.component.html',
  styles: []
})
export class TimesheetComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  smallScreen = false;
  items: MenuItem[] = [{label: "Timesheet", icon: "pi-fw fas fa-user-clock"}];

  spinnerName: string = "timesheetSpinner";
  defaultSpinnerText: string = "Loading timesheet data ...";
  spinnerText: string = "Loading timesheet data ...";
  
  private gridApi;
  private gridColumnApi;
  exporting: boolean = false;
  exportingValidationReport : boolean = false;
  showSideBar: boolean = true;

  private warningColumns = ["message","payrollRosterCode","totalHours"];

  isLoggedIn: boolean = false;
  userContext: UserContext = null;

  selectedRow: any = null;
  selectedRowNode: any = null; //ag-grid RowNode

  gridHeaders = [];
  context:any = {};
  timesheets: Timesheet[] = [];
  allTimesheets: Timesheet[] = [];
  missingTimesheets: Timesheet[] = [];
  timesheetsTotal: Timesheet[] = [];

  jobCodes: IJobCode[] = [];
  selectedJobCode: IJobCode = null;

  groups: IEntity[] = [];
  selectedGroup: IEntity = null;

  employees: EmployeeBase[] = [];
  selectedEmployee: EmployeeBase = null;

  employers: IEmployer[] = [];
  selectedEmployer: IEmployer = null;

  quickDateRanges: {id:string, title: string}[] = [];
  selectedDateRange: {id:string, title: string} = null;

  rosterCodes: IRosterCode[] = [];
  searchModel: TimesheetSearchModel = {
    fromDate: new Date(),
    toDate: new Date(),
    groupId: 0,
    employeeId: 0,
    jobCodeId: 0,
    employerId: 0
  };

  topOptions = {
    alignedGrids: [],
    getRowId: param => {
      //console.log(param.data);
      return `${param.data.employee.id}-${param.data.id ?? 0 }`;
    },
    //immutableData: true,
    suppressHorizontalScroll: true,
    defaultColDef:{
      resizable: true,
      floatingFilter: true
    }
  };

  bottomOptions = {
    alignedGrids: []
  };

  private dateFilterParams = {
    comparator: this._util.filterDateComparator,
    filterOptions: ["equals", "lessThan", "greaterThan"],
    defaultOption: "greaterThan"
  };

  private numberFilterParams = {
    filterOptions: ["equals", "lessThan", "greaterThan"],
    defaultOption: "equals"
  };

  constructor(private timesheetService: TimesheetService,
              private spinner: NgxSpinnerService,
              private _dialog: DialogService,
              private _confirmDialog: ConfirmationService,
              public authService: AuthenticationService,
              private toast: ToastService,
              public _util: PpsUtilityService,
              private breakpointObserver: BreakpointObserver) {
      this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
        .subscribe((state: BreakpointState) => {
          this.smallScreen = !state.matches;
        });    

      this.topOptions.alignedGrids.push(this.bottomOptions);
      this.bottomOptions.alignedGrids.push(this.topOptions);
    }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnDestroy(): void{
    this.subs.unsubscribe();    
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();

    this.selectedDateRange = {id: "today", title: "Today"};

    this.loadEntities();
  }

  onGridReady(gridReadyEvent: GridReadyEvent) {
    this.gridApi = gridReadyEvent.api;
    this.gridColumnApi = gridReadyEvent.columnApi;

    if (!this.canModifyTimeSheet() && !this.canModifyPastTimeSheet()){
      gridReadyEvent.columnApi.setColumnVisible("action", false);
    }
  }

  onRowClicked(evt){
    let _this = evt.context.componentParent;
    var selectedRows = _this.gridApi.getSelectedRows();
    _this.selectedRow = selectedRows.length === 1 ? selectedRows[0] : null;
    _this.selectedRowNode = evt.node;
  }  

  onRowDblClicked(params){
    let timesheet = params.data as Timesheet;

    if (timesheet.warningType !== WarningType.MissingTimesheet && (this.canModifyTimeSheet() || this.canModifyPastTimeSheet())){
      this.openTimesheetForm(timesheet);
    }
  }

  loadTimesheet(){
    this.spinnerText = "Load timesheet data...";
    this.spinner.show(this.spinnerName);

    this.timesheets = [];

    this.subs.sink = this.timesheetService.getTimesheets(this.searchModel)
      .subscribe(response => {
        if (response.succeed){
          this.allTimesheets = response.result;
          this.showAllEnteredTimesheets();
        }
      })
      .add(() => {
        this.spinner.hide(this.spinnerName);
        this.spinnerText = this.defaultSpinnerText;
      });
  }

  exportTimesheet(){
    this.exporting = true;
    this.subs.sink = this.timesheetService.downloadTimesheets(this.searchModel, false, () => this.exporting = false);
  }

  timesheetValidationReport(){
    this.exportingValidationReport = true;
    this.subs.sink = this.timesheetService.downloadTimesheets(this.searchModel, true, () => this.exportingValidationReport = false);
  }
  
  onEmployerChanged(){
    this.searchModel.employerId = this.selectedEmployer?.id ?? 0;

    this.loadTimesheet();
  }

  onJobCodeChanged(){
    if (this.selectedJobCode){
      this.searchModel.jobCodeId = this.selectedJobCode.id;
    }else{
      this.searchModel.jobCodeId = 0;
    }

    this.loadTimesheet();
  }

  onGroupChanged(event){
    if (this.selectedGroup){
      this.searchModel.groupId = this.selectedGroup.id;
      this.selectedEmployee = null;
      this.searchModel.employeeId = 0;
    }else{
      this.searchModel.groupId = 0;
    }

    this.loadTimesheet();
  }

  onEmployeeChanged(){
    if (this.selectedEmployee){
      this.searchModel.employeeId = this.selectedEmployee.id;
      this.selectedGroup = null;
      this.searchModel.groupId = 0;
    }else{
      this.searchModel.employeeId = 0;
    }

    this.loadTimesheet();
  }

  onDateRangeChanged(event){
    if (this.selectedDateRange){
      let range = this._util.getDateRange(this.selectedDateRange.id);
      if (range){
        this.searchModel.fromDate = range.from;
        this.searchModel.toDate = range.to;
        this.loadTimesheet();
      }
    }
  }

  manageTimesheet(): void{
    let dialog = this._dialog.open(ManageTimesheetComponent, {
      header: "Employee Timesheet",
      styleClass: "size-md",
      data:{
        parent: this,
        groups: this.groups,
        rosterCodes: this.rosterCodes,
        employees: this.employees,
        jobCodes: this.jobCodes
      },
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(
      _ => {
      },
      reason => {
      }
    );
  }

  getHoursEntered(empId: number, day: Date){
    let hoursEntered = 0;
    this.timesheets.forEach(ts => {
      if (isSameDay(ts.day, day) && ts.employee.id === empId){
        hoursEntered += ts.hours;
      }
    });

    return hoursEntered;
  }

  openTimesheetForm(timesheet: Timesheet){
    let hoursEntered = 0;
    this.timesheets.forEach(ts => {
      if (isSameDay(ts.day, timesheet.day) && ts.employee.id === timesheet.employee.id){
        hoursEntered += ts.hours;
        console.log(ts);
      }
    });

    let dialog = this._dialog.open(TimesheetFormComponent, {
      header: `Edit Timesheet - ${this._util.getEmployeeName(timesheet.employee)}`,
      styleClass: "size-sm",
      data:{
        parent: this,
        timesheet: Object.assign({}, timesheet),
        jobCodes: this.jobCodes,
        rosterCodes: this.rosterCodes
      },
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(
      timesheet => {
        if (timesheet){
          this.saveTimesheet(timesheet);
        }
      },
      reason => {
      }
    );
  }

  saveTimesheet(timesheet: Timesheet){
    this.subs.sink = this.timesheetService
      .updateTimesheet(timesheet)
      .subscribe(response => {
        if (response.succeed){
          let foundIndex = this.allTimesheets.findIndex(x => x.id == timesheet.id);
          if (foundIndex !== -1 ){
            this.allTimesheets[foundIndex] = response.result;
          }

          foundIndex = this.timesheets.findIndex(x => x.id == timesheet.id);
          if (foundIndex !== -1 ){
            this.timesheets[foundIndex] = response.result;
            this.timesheetsTotal = this.getTimesheetTotal(this.timesheets);
            var rowId = `${timesheet.employee.id}-${timesheet.id ?? 0 }`;
            this.resetGrid(this.timesheets[foundIndex], timesheet.id , rowId, false);
            this.flashRow(timesheet.id);
            this.toast.succeeded("Update timesheet succeed!");
          }else{
            this.toast.failed("Update timesheet failed!");
          }
        }else{
          this.toast.failed("Failed to save timesheet [" + response.errors.join("|") + "]");
        }
      });
  }

  getCountOfAllEnteredTimesheets(){
    return this.allTimesheets.filter(t=>t.warningType !== WarningType.MissingTimesheet).length;
  }  

  getCountOfTimesheetsWithWarning(){
    return this.allTimesheets.filter(ts =>ts.warningType !== WarningType.NoWarning && ts.warningType !== WarningType.MissingTimesheet).length;
  }  

  getCountOfMissingTimesheets(){
    return this.allTimesheets.filter(t=>t.warningType === WarningType.MissingTimesheet).length;
  }

  showAllEnteredTimesheets(){
    this.showMessageColumns(false);
    this.timesheets = this.allTimesheets.filter(t=>t.warningType !== WarningType.MissingTimesheet);
    this.timesheetsTotal = this.getTimesheetTotal(this.timesheets);
  }

  showTimesheetsWithWarning(){
    this.showMessageColumns(true);
    this.timesheets = this.allTimesheets.filter(ts =>ts.warningType !== WarningType.NoWarning && ts.warningType !== WarningType.MissingTimesheet);
    this.timesheetsTotal = this.getTimesheetTotal(this.timesheets);
  }

  showMissingTimesheets(){
    this.showMessageColumns(true);
    this.timesheets = this.allTimesheets.filter(t=>t.warningType === WarningType.MissingTimesheet);
    this.timesheetsTotal = this.getTimesheetTotal(this.timesheets);
  }

  showMessageColumns(show: boolean){
    var state = [{colId: "message", hide: !show}];

    this.gridColumnApi.applyColumnState({
      state: state
    });
  }
  //
  // This is to update the values of the  grid and make sure the selected
  // row is rendered correctly
  private resetGrid(timesheet: Timesheet, timesheetId: number, selectedRowId: string, ensureVisible: boolean){
    // reference:
    // https://stackoverflow.com/questions/55723337/ag-grid-how-to-scroll-to-last-known-position
    // if ( ensureVisible ){
    //   this.topOptions.immutableData = false;
    // }

    let rowNode = this.gridApi.getRowNode(selectedRowId);
    rowNode.setData(timesheet);

    if (timesheetId !== 0 && ensureVisible){
      var node = this.gridApi.getRowNode(selectedRowId);
      if (rowNode){
        this.gridApi.ensureIndexVisible(rowNode.rowIndex, 'middle');
        rowNode.setSelected(true);
      }
    }

    //if ( ensureVisible ){
      //this.topOptions.immutableData = true;
    //}
  }

  deleteTimesheet(timesheet: Timesheet){
    if (timesheet === null){
      return;
    }

    this._confirmDialog.confirm({
        header: "Delete Timesheet",
        message: `Please confirm the deletion of the timesheet for: ${this._util.getEmployeeName(timesheet.employee)}?`,
        icon: "fas fa-exclamation-circle icon-danger",
        defaultFocus: "accept",
        accept: () => {
          this.subs.sink = this.timesheetService.deleteTimesheet(timesheet.id)
            .subscribe(res => {
              if (res.succeed){
                var foundIndex = this.timesheets.findIndex(x => x.id == timesheet.id);
                if (foundIndex !== -1 ){
                  this.timesheets.splice(foundIndex, 1);
                  this.timesheetsTotal = this.getTimesheetTotal(this.timesheets);
                  this.gridApi.setRowData(this.timesheets);

                  if (foundIndex < this.timesheets.length){
                    let nextRow = this.gridApi.getRowNode(this.timesheets[foundIndex].id);
                    if (nextRow){
                      nextRow.setSelected(true);
                      this.selectedRow = this.timesheets[foundIndex];
                      this.selectedRowNode = nextRow;
                    }
                  }
      
                  this.toast.succeeded("Timesheet deleted!");
                }else{
                  this.toast.failed("Delete timesheet failed!");
                }
              }else{
                this.toast.failed("Failed to delete timesheet [" + res.errors.join("|") + "]");
              }
            })
        }});
  }

  generateTimesheet(){
    let dialog = this._dialog.open(GenerateTimesheetComponent, {
      header: "Generate Employee Timesheet from Roster",
      styleClass: "size-md",
      data:{
        parent: this,
        groups: this.groups,
        rosterCodes: this.rosterCodes,
        employees: this.employees,
        jobCodes: this.jobCodes
      },
      draggable: true
    });

    this.subs.sink = dialog.onClose.subscribe(
      timesheetGenerationModel => {
        if (timesheetGenerationModel){
          this.generateTimesheetHelper(timesheetGenerationModel);
        }
      },
      reason => {
      }
    );
  }

  generateTimesheetHelper(timesheetGenerationModel: TimesheetGenerationModel){
    this.spinnerText = "Generating timesheet data...";
    this.spinner.show(this.spinnerName);

    this.subs.sink = this.timesheetService.generateTimesheet(timesheetGenerationModel)
      .subscribe(res=>{
        if (res.succeed && res.result > 0){
          this.searchModel = {
            fromDate: timesheetGenerationModel.from,
            toDate: timesheetGenerationModel.to,
            groupId: 0,
            employeeId: 0,
            jobCodeId: 0,
            employerId: 0
          };
        }
      },
      err => {},
      () => {
        this.spinner.hide(this.spinnerName);
        this.spinnerText = this.defaultSpinnerText;
        this.loadTimesheet();
      });
  }

  canEnterTimeSheet(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.EnterTimeSheet);
  }

  canModifyTimeSheet(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.ModifyTimeSheet);
  }

  canExportTimeSheet(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.ExportTimeSheet);
  }

  canModifyPastTimeSheet(): boolean{
    let result = this.authService.canUserDoOperartion(BusinessOperation.UpdatePastTimeSheet);
    return result;
  }

  canGenerateTimeSheet(): boolean{
    return this.authService.canUserDoOperartion(BusinessOperation.GenerateTimeSheet);
  }

  flashRow(id){
    let rowNode = this.gridApi.getRowNode(id);
    if (rowNode){
      this.gridApi.flashCells({rowNodes: [rowNode]});
    }    
  }

  private getColumnDefs(){
    let colDefs = [
      {
        headerName: "Actions",
        colId: "action",
        field: "value",
        cellRenderer: TimesheetActionComponent,
        width: 80,
        pinned: true,
        resizable: false,
        cellStyle: {textAlign:"center"}
      },
      {
        headerName: 'Employee',
        colId: "employee",
        field:'employee',
        filter: true,
        sortable: true,
        width: 180,
        pinned: true,
        cellStyle: this.getCellStyle,
        tooltipValueGetter: (params) => {
          return params.data.warningMessage || "";
        },
        valueGetter: params => params.context.componentParent._util.getEmployeeName(params.data[params.colDef.field])
      },
      {
        headerName: 'Message',
        colId: "message",
        field:'warningMessage',
        filter: true,
        sortable: true,
        width: 360,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Position',
        colId: "position",
        field:'employee.position.title',
        filter: true,
        sortable: true,
        cellStyle: this.getCellStyle,
        width: 160
      },
      {
        headerName: 'Job Code',
        colId: "jobcode",
        field:'jobCode.jobCodeNo',
        filter: true,
        sortable: true,
        width: 100,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Date',
        colId: "day",
        field:'day',
        filter: 'agDateColumnFilter',
        filterParams: this.dateFilterParams,
        sortable: true,
        width: 170,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        valueGetter: this.getDateValue,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Day',
        colId: "weekday",
        field:'day',
        filter: true,
        sortable: true,
        width: 80,
        valueGetter: this.getWeekDay,
        comparator: (va, vb, na, nb, inverted) => {
          let a = this._util.getDate(na.data.day).getDay();
          let b = this._util.getDate(nb.data.day).getDay();

          if (a === b) return 0;
          return (a > b) ? 1 : -1;
        },
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Hours',
        colId: 'hours',
        field: 'hours',        
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        sortable: true,
        width: 80,
        cellStyle: this.getCellStyle,
        cellRenderer: params => params.context.componentParent._util.formatNumber(params.data[params.colDef.field], 2)
      },
      {
        headerName: 'Shift',
        colId: 'shift',
        field:'shift.code',
        filter: true,
        sortable: true,
        width: 70,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Group',
        colId: 'group',
        field:'employee.workGroup.name',
        filter: true,
        sortable: true,
        width: 250,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Note',
        colId: 'note',
        field:'note',
        filter: true,
        sortable: true,
        width: 280,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Company',
        colId: 'company',
        field:'employee.employer.employerName',
        filter: true,
        sortable: true,
        width: 80,
        cellStyle: this.getCellStyle
      }/*,
      {
        headerName: 'Payroll Roster Code',
        colId: 'payrollRosterCode',
        field:'payrollRosterCode',
        filter: true,
        sortable: true,
        width: 140,
        cellStyle: this.getCellStyle
      },
      {
        headerName: 'Total Hours',
        colId: "totalHours",
        field:'totalHours',        
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        sortable: true,
        width: 120,
        cellStyle: this.getCellStyle,
        cellRenderer: params => params.context.componentParent._util.formatNumber(params.data[params.colDef.field], 2)
      },
      {
        headerName: 'Warning Type',
        colId: "warningType",
        field:'warningType',
        width: 180,
        filter: 'agNumberColumnFilter',
        hide: true
      }*/
    ];

    if (this.smallScreen){
      colDefs.splice(0, 1);
      colDefs[0].pinned = false;
    }

    return colDefs;
  }

  private getTimesheetTotal(timesheets: Timesheet[]){
    let total:Timesheet = new Timesheet();
    total.employee = new Employee();
    total.employee.firstName = "Total";
    total.hours = 0;
    total.id = 0;
    timesheets.forEach(t => total.hours += t.hours);

    return [total];
  }

  private loadEntities(){
    this.subs.sink = this.timesheetService.getTimesheetEntities()
      .subscribe(res => {
        if (res.succeed){
          this.employees = res.result.employees;
          this.groups = this.getGroups(this.employees);
          this.jobCodes = res.result.jobCodes;
          this.rosterCodes = res.result.rosterCodes.filter(rc => rc.canBeUsedInTimesheet);
          this.employers = res.result.employers.sort((a,b) => this._util.sortAsc(a, b, "employerName"));

          let dateRanges = this._util.getDateRanges();
          this.quickDateRanges = Object.keys(dateRanges).map(key => { return {id: key, title: dateRanges[key].title}; });

          this.loadTimesheet();
        }
      });
  }

  private getGroups(employees: EmployeeBase[]): IEntity[]{
    let groupDict = {};
    employees.forEach(emp => {
      if (emp.workGroup && !groupDict.hasOwnProperty(emp.workGroup.id)){
        groupDict[emp.workGroup.id] = Object.assign({}, emp.workGroup);
      }
    });

    let groups: IEntity[] = Object.values(groupDict);
    groups.sort((a, b) => a.name > b.name ? 1 : (a.name < b.name ? -1 : 0));
    
    return groups;

  }

  private getWeekDay(params){
    var dateValue = params.data[params.colDef.field];
    if (dateValue){
      return format(dateValue, "E");
    }
  }

  private getDateValue(params){
    if (params.data[params.colDef.field]){
      let value = params.context.componentParent._util.getDateString(params.data[params.colDef.field]);
      return value;
    }

    return "";
  }

  private getCellStyle(params){
    var timesheet = params.data as Timesheet;
    var colId = params.colDef.colId;
    var style = {backgroundColor: "rgba(255,255,255,0)", color: "#000000"};
    let _this = params.context.componentParent;

    if (colId === "hours" || colId === "totalHours"){
      style = Object.assign(style, _this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}));
    }

    if (timesheet.warningMessage && (_this.warningColumns.includes(colId) || colId === "employee")){
      if (timesheet.warningType === WarningType.MissingTimesheet){
        style = Object.assign(style, {backgroundColor: "#ff0000", color: "#ffff00"});
      }
      else{
        style = Object.assign(style, {backgroundColor: "#FBC02D", color: "#212529"});
      }
    }

    return style;
  }
}